import React, { forwardRef, useCallback } from 'react';

import { useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { DOMKeyframesDefinition, DynamicAnimationOptions, motion, useAnimate } from 'framer-motion';

export const usePlayStarConfetti = () => {
  const [scope, animate] = useAnimate();

  const play = useCallback(
    (isExtendedConfetti?: boolean) => {
      animate(
        '.starPath',
        { offsetDistance: ['35%', '100%'], opacity: [0, 1, 0] },
        { duration: 0.9, ease: 'easeOut' },
      );
      if (isExtendedConfetti) {
        animate(...getLineAnimation('.line'));
        animate(...getLineAnimation('.line2', 0.2));
        animate(...getCurvedLineAnimation('.curvedLine'));
        animate(...getCurvedLineAnimation('.curvedLine2', 0.3));
      }
    },
    [animate],
  );

  return { scope, play };
};

const getLineAnimation = (
  selector: string,
  delaySec: number = 0,
): [string, DOMKeyframesDefinition, DynamicAnimationOptions] => {
  return [
    selector,
    {
      opacity: [0, 1],
      pathLength: [0, 1],
      x: [0, 2],
      y: [0, 2],
      pathOffset: [0, 1],
    },
    {
      opacity: { delay: delaySec, duration: 0 },
      pathLength: { delay: delaySec, duration: 0.3 },
      x: { delay: delaySec + 0.25, duration: 0.1 },
      y: { delay: delaySec + 0.25, duration: 0.1 },
      pathOffset: { delay: delaySec + 0.3, duration: 0.3 },
    },
  ];
};

const getCurvedLineAnimation = (
  selector: string,
  delaySec: number = 0,
): [string, DOMKeyframesDefinition, DynamicAnimationOptions] => {
  return [
    selector,
    {
      opacity: [0, 1, 0],
      pathLength: [0, 1],
      pathOffset: [0, 1],
    },
    {
      opacity: { delay: delaySec, duration: 0.8 },
      pathLength: { delay: delaySec, duration: 0.4 },
      pathOffset: { delay: delaySec + 0.3, duration: 0.4 },
    },
  ];
};

interface StarConfettiProps {
  className?: string;
}

const StarConfetti = forwardRef<HTMLDivElement, StarConfettiProps>(({ className }, ref) => {
  const theme = useTheme();
  const primaryColor = theme.palette.primary.light;
  const secondaryColor = theme.palette.common.blue;

  return (
    <InternalConfettiWrapper ref={ref} className={className} data-testid="InternalConfettiWrapper">
      {[
        { initial: { x: 20, y: 7 }, color: primaryColor },
        { initial: { x: 15, y: 10, scale: '-1, -1' }, color: primaryColor },
        { initial: { x: 17, y: 5, scale: '-1,1' }, sizePx: 3, color: secondaryColor },
        { initial: { x: 20, y: 11, scale: '1,-1' }, sizePx: 3, color: secondaryColor },
        { initial: { x: 21, y: 10, rotate: 105 }, sizePx: 3, color: secondaryColor },
        { initial: { x: 15, y: 6, scale: '-1,-1', rotate: 105 }, sizePx: 3, color: secondaryColor },
      ].map((star, index) => (
        <DivWrapper data-testid="StyledDivWrapper" initial={star.initial} key={index}>
          <StarSvg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            initial={{
              offsetDistance: '35%',
              opacity: 0,
              width: star.sizePx || 5,
              height: star.sizePx || 5,
            }}
            className="starPath"
          >
            <path
              d="M11.5093 0.290046C11.7221 -0.0966817 12.2779 -0.096682 12.4907 0.290045L15.2983 5.39191C16.0657 6.78646 17.2135 7.93428 18.6081 8.70172L23.71 11.5093C24.0967 11.7221 24.0967 12.2779 23.71 12.4907L18.6081 15.2983C17.2135 16.0657 16.0657 17.2135 15.2983 18.6081L12.4907 23.71C12.2779 24.0967 11.7221 24.0967 11.5093 23.71L8.70171 18.6081C7.93428 17.2135 6.78646 16.0657 5.39191 15.2983L0.290046 12.4907C-0.0966817 12.2779 -0.096682 11.7221 0.290045 11.5093L5.39191 8.70171C6.78646 7.93428 7.93428 6.78646 8.70172 5.39191L11.5093 0.290046Z"
              fill={star.color}
            />
          </StarSvg>
        </DivWrapper>
      ))}

      <svg
        width="40"
        height="40"
        viewBox="-20 -20 40 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {[
          { className: 'line', color: primaryColor },
          { className: 'line', initial: { rotate: 180, x: -24, y: -22 }, color: primaryColor },
          { className: 'line2', initial: { x: 2, y: 7 }, color: secondaryColor },
          { className: 'line2', initial: { rotate: 180, x: -25, y: -17 }, color: secondaryColor },
        ].map((line, index) => (
          <motion.g initial={line.initial} key={index}>
            <motion.path
              d="M9,6 L14,11"
              stroke={line.color}
              strokeWidth="1"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={line.className}
              initial={{
                opacity: 0,
                pathLength: 0,
                x: 0,
                y: 0,
                pathOffset: 0,
              }}
            />
          </motion.g>
        ))}

        {[
          { className: 'curvedLine', color: secondaryColor, mirror: false },
          {
            className: 'curvedLine2',
            color: primaryColor,
            mirror: true,
          },
        ].map((curvedLine, index) => (
          <g transform={`scale(0.6) rotate(${curvedLine.mirror ? 180 : 0})`} key={index}>
            <motion.path
              d="M2.1441 17.25C-5.96046e-08 7.5 7.42876 5.57075 10.6439 8.25C13.6439 10.75 10.9723 14.6642 8.14389 13.25C4.14393 11.25 2.1441 2.25 12.645 1.75"
              stroke={curvedLine.color}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={curvedLine.className}
              initial={{ opacity: 0, pathLength: 0, pathOffset: 0, x: 13, y: -31 }}
            />
          </g>
        ))}
      </svg>
    </InternalConfettiWrapper>
  );
});

export default StarConfetti;

export const StarConfettiWrapper = styled('span')`
  display: flex;
  position: relative;
  align-items: center;
`;
const InternalConfettiWrapper = styled('div')`
  position: absolute;
  display: flex;
`;
const DivWrapper = styled(motion.div)`
  position: absolute;
`;
const StarSvg = styled(motion.svg)`
  offset-path: path('M0,0 Q5,-14 -7,-18');
`;
